import { until } from 'lit-html/directives/until';
import { repeat } from 'lit-html/directives/repeat';
import '@modules/role-crud-block';
import '@modules/role-view';
import '@modules/aofl-form';
import { generalLabels, toastStatus } from '@modules/i18n-labels';
import { Tooltip, LoadingEllipses } from '@epg/epg-components';
import { userTypes } from '@modules/constants-enumerate';

const learningAssistantStatuses = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  UNSET: 'unset',
};
const getToggleLabel = (enabled, ctx, html) => {
  return html`<span class="font-weight-normal">${until(ctx.__('<tt-iU7xdllF>', (enabled) ? 'On' : 'Off'))}</span>`;
};

const getLearningAssistantSetting = (ctx, html) => {
  if (ctx.data.profile.type !== userTypes.ADMIN || ctx.userId !== ctx.loggedInUserId) {
    return;
  }
  return html`
  <div class="col-md-12 col-lg-12 form-group row align-items-center">
    <label class="col-form-label"
            for="learning-assistant-feature">${until(ctx.__('<tt-YD2a4XwD>', 'Mastery AI Assistant™:'))}</label>
    <div class="mx-2 p-0 col-form-label">
      <div class="form-check-inline form-switch">
        <input class="form-check-input" type="checkbox"
                id="learning-assistant-feature"
                name="learning-assistant-feature"
                @change="${ctx.learningAssistantUpdated}"
                .checked="${ctx.learningAssistantEnabled}"
                ?disabled=${ctx.data.profile.type !== userTypes.ADMIN || ctx.learningAssistantStatus === learningAssistantStatuses.DISABLED}
        >
        ${getToggleLabel(ctx.learningAssistantEnabled, ctx, html)}
        <tool-tip
          class="ml-3"
          .title="${until(getTextForInfoTooltip(ctx))}"
          .open_tooltip_id="${ctx.tooltipId}"
          tooltip_id="learning-assistant-tooltip"
          @closeToolTip=${(e) => ctx.setTooltipId(e, '')}
        >
          <button
            class="btn btn-link p-0 line-height-1"
            @click="${(e) => ctx.setTooltipId(e, 'learning-assistant-tooltip')}">
            <span class="ai ai-info cursor-pointer"></span>
          </button>
        </tool-tip>
        ${(ctx.savedLearningAssistant) ?
          html`
            <div class="alert alert-success py-2 px-3 ml-3 mb-0 rounded">
              <img class="mr-2" src="${require('@assets/alert-icons/success.svg')}" alt="success"/>${until(ctx.__('<tt-mFIXvSsd>', 'Saved!'))}
            </div>` :
          html``}
      </div>
    </div>
  </div>
  <hr class="d-none">
  `;
};

const getTextForInfoTooltip = (ctx) => {
  if (ctx.data.profile.type !== userTypes.ADMIN) {
    return;
  }
  if (ctx.isRootAdmin) {
    return `You can choose to enable or disable this feature for your organization. If enabled, lower-level administrators will be able to manage access within their own scope.<br><br>Privacy Note: This AI-driven feature does not use any student personal data. It generates content based on students' learning progress in our program.`;
  }
  if (ctx.learningAssistantStatus === learningAssistantStatuses.DISABLED) {
    return `This feature has been turned off by your higher-level organization administrator. To enable it for your site, please contract your higher-level organization administrator.`;
  }
  if (ctx.learningAssistantStatus === learningAssistantStatuses.ENABLED) {
    return `This feature is controlled by your higher-level organization administrator. If it’s turned it off at that level, you won’t be able to enable it for your site level. If it’s turned on, you’ll have the option to enable or disable it here.<br><br>Privacy Note: This AI-driven feature does not use any student personal data. It generates content based on students\' learning progress in our program.`;
  }
  // not set above current level
  if (ctx.isSchoolAdmin) {
    return `You can choose to enable or disable this feature for your organization. If enabled, lower-level administrators will be able to manage access within their own scope.<br><br>Privacy Note: This AI-driven feature does not use any student personal data. It generates content based on students\' learning progress in our program.`;
  }
  return `You can choose to enable or disable this feature for your organization. If enabled, lower-level administrators will be able to manage access within their own scope.<br><br>Privacy Note: This AI-driven feature does not use any student personal data. It generates content based on students\' learning progress in our program.`;
};

export default (ctx, html) => html`
  ${ctx.disabled && !ctx.isRootAdmin
    ? html`
      <div class="alert alert-warning">
        ${ctx.hasPerms ? until(toastStatus.saveFailTpRosteringConnected) : until(toastStatus.insufficentPermissionsEdit)}
      </div>`
    : ''}
  <aofl-form>
    <form class="horizontal-form" @submit="${ctx.submit}" novalidate autocomplete="off">
      <div class="row mb-3">
        <div class="col-md-12 col-lg-6">
          <div class="form-group row">
            <label for="first-name"
                   class="col-sm-4 col-form-label">${until(ctx.__('<tt-qNSbxNb2>', '<span aria-hidden="true">*</span>First Name'))}</label>
            <div class="col-sm-8">
              <input name="first-name"
                     class="form-control  ${!ctx.form.firstName.valid ? 'is-invalid' : ''}"
                     id="first-name"
                     @input="${ctx.firstNameUpdated}"
                     @blur="${ctx.validateFirstName}"
                     required
                     ?disabled="${ctx.disabled}"
                     .value="${ctx.firstName}">
            </div>
            ${!ctx.form.firstName.isRequired.valid ? html`<p class="invalid-feedback col-sm-8 offset-sm-4 d-block mb-0">
              ${until(ctx.__('<tt-QD4ST6gV>', 'First name is required.'))}</p>` : ''}
          </div>
        </div>
        <div class="col-md-12 col-lg-6">
          <div class="form-group row">
            <label for="last-name"
                   class="col-sm-4 col-form-label">${until(ctx.__('<tt-nCRF2Yp5>', 'Last Name'))}</label>
            <div class="col-sm-8">
              <input
                name="last-name"
                class="form-control"
                id="last-name"
                @input="${ctx.lastNameUpdated}"
                .value="${ctx.lastName}"
                ?disabled="${ctx.disabled}"
              >
            </div>
          </div>
        </div>
        <div class="col-md-12 col-lg-6">
          <div class="form-group row">
            <label for="email" class="col-sm-4 col-form-label">${until(generalLabels.loginEmail)}</label>
            <div class="col-sm-8">
              <input type="email" name="email" ?disabled="${ctx.showProfile || ctx.showContactInfo || ctx.disabled}"
                     class="form-control ${!ctx.form.email.valid ? 'is-invalid' : ''}" id="email"
                     @input="${ctx.emailUpdated}" @blur="${ctx.validateEmailAndPhone}" .value="${ctx.email}">
            </div>
            ${!ctx.form.email.valid ? html`<p class="invalid-feedback col-sm-8 offset-sm-4 d-block mb-0">
              ${until(ctx.__('<tt-rpFqFZLA>', 'An email address or a phone number is required.'))}</p>` : ''}
            ${(!ctx.form.email.emailLike.valid) ? html`<p class="invalid-feedback col-sm-8 offset-sm-4 d-block mb-0">
              ${until(generalLabels.invalidEmailError)}</p>` : ''}
          </div>
        </div>
        <div class="col-md-12 col-lg-6">
          <div class="form-group row">
            <label for="phone" class="col-sm-4 col-form-label">${until(generalLabels.loginPhone)}</label>
            <div class="col-sm-8">
              <phone-input
                name="phone"
                id="phone"
                .initialValue="${ctx.phone}"
                @click="${ctx.phoneUpdated}" @keyup="${ctx.phoneUpdated}" @blur="${ctx.validateEmailAndPhone}"
                class="form-control ${!ctx.form.phone.phoneLike.valid ? 'is-invalid' : ''}"
                style="${(ctx.showProfile || ctx.showContactInfo) ? 'background-color: #e9ecef;' : ''}"
                ?disabled="${ctx.showProfile || ctx.showContactInfo || ctx.disabled}"
                .value="${ctx.phone}"></phone-input>
            </div>
            ${!ctx.form.phone.phoneLike.valid ? html`<p class="invalid-feedback col-sm-8 offset-sm-4 d-block mb-0">
              ${until(generalLabels.invalidPhoneError)}</p>` : ''}
          </div>
        </div>
        ${ctx.showContactInfo ? html`
          <div class="col-md-12 col-lg-6">
            <div class="form-group row">
              <label for="contact-email"
                     class="col-sm-4 col-form-label">${until(ctx.__('<tt-1ekrmUw7>', '<span aria-hidden="true">*</span>Contact Email'))}</label>
              <div class="col-sm-8">
                <input type="email" name="contact-email" id="contact-email"
                       class="form-control ${!ctx.form.contact_email.valid ? 'is-invalid' : ''}"
                       ?disabled="${ctx.userType === userTypes.TEACHER || ctx.disabled}"
                       required
                       @input="${ctx.contactEmailUpdated}" @blur="${ctx.validateContactEmailAndPhone}"
                       .value="${ctx.contactEmail}">
              </div>
              ${!ctx.form.contact_email.valid ? html`<p class="invalid-feedback col-sm-8 offset-sm-4 d-block mb-0">
                ${until(ctx.__('<tt-KEvnQRCV>', 'An email address or a phone number is required.'))}</p>` : ''}
              ${(!ctx.form.contact_email.emailLike.valid) ? html`<p
                class="invalid-feedback col-sm-8 offset-sm-4 d-block mb-0">
                ${until(generalLabels.invalidEmailError)}</p>` : ''}
            </div>
          </div>
          <div class="col-md-12 col-lg-6">
            <div class="form-group row">
              <label for="contact-phone"
                     class="col-sm-4 col-form-label">${until(ctx.__('<tt-9EPJnmak>', 'Contact Phone'))}</label>
              <div class="col-sm-8">
                <phone-input
                  name="contact-phone"
                  id="contact-phone"
                  .initialValue="${ctx.contactPhone}"
                  @click="${ctx.contactPhoneUpdated}" @keyup="${ctx.contactPhoneUpdated}"
                  @blur="${ctx.validateContactEmailAndPhone}"
                  class="form-control ${!ctx.form.contact_phone.phoneLike.valid ? 'is-invalid' : ''}"
                  .value="${ctx.contactPhone}"
                  style="${(ctx.disabled) ? 'background-color: #e9ecef;' : ''}"
                  ?disabled="${ctx.disabled}"
                ></phone-input>
              </div>
              ${!ctx.form.contact_phone.phoneLike.valid ? html`<p
                class="invalid-feedback col-sm-8 offset-sm-4 d-block mb-0">
                ${until(generalLabels.invalidPhoneError)}</p>` : ''}
            </div>
          </div>
        ` : ''}
        <div class="col-md-12 col-lg-6">
          <div class="form-group row">
            <label for="external-id"
                   class="col-sm-4 col-form-label">${until(ctx.__('<tt-mqj2eoYA>', 'Unique Roster ID'))}</label>
            <div class="col-sm-8">
              <input type="text" name="external-id" id="external-id"
                     class="form-control ${!ctx.form.external_id.valid ? 'is-invalid' : ''}"
                     @input="${ctx.externalIdUpdated}"
                     .value="${ctx.externalId}"
                     ?disabled="${ctx.disabled}"
              >
            </div>
            ${!ctx.form.external_id.valid ? html`<p class="invalid-feedback col-sm-8 offset-sm-4 d-block mb-0">
              ${until(generalLabels.externalIdInUseError)}</p>` : ''}
          </div>
        </div>
      </div>
      ${!ctx.isRootAdmin ? html`
        <button type="submit" class="btn btn-lg btn-block btn-primary rounded-pill mx-auto"
        ?disabled="${!ctx.form.observed || !ctx.form.valid || ctx.saving || ctx.isRootAdmin}">
        ${until(generalLabels.save)}
          ${ctx.saving ? html`<div class="spinner-border" role="status"><span class="sr-only"></span></div>` : ``}
        </button>
      ` : html``}
    </form>
  </aofl-form>

  <slot></slot>
  <hr>
  ${getLearningAssistantSetting(ctx, html)}
  <div class="d-flex items-center justify-content-between mb-3 w-full">
    <h2 class="h3 mb-0 font-weight-bold">${until(ctx.__('<tt-2344bVbr>', 'Roles'))}</h2>
    ${ctx.userId !== ctx.loggedInUserId ? html`
    <button class="btn btn-primary"
          @click=${ctx.saveRoles}
          ?disabled="${!ctx.userId}">
      ${until(ctx.__('<tt-xoPjQvHU>', 'Save Roles'))}
      ${ctx.savingRoles ? html`<div class="spinner-border" role="status"><span class="sr-only"></span></div>` : ``}
      ${ctx.rolesSavedSuccessfully && !ctx.savingRoles ? html`<span class="ai ai-checkmark"></span>` : ``}
    </button>
    ` : html``}
  </div>

  ${repeat(ctx.pendingRoles, (role) => role.tempId, (role, index) => html`
    <div class="row bm-5">
      <div class="col-12">
        <role-crud-block index=${index}></role-crud-block>
      </div>
    </div>
    <hr>
  `)}

  ${repeat(ctx.roles, (role) => role.id, (role) => html`
    <role-view class="mb-4" user-id="${ctx.userId}" .role="${role}" .user-id="${ctx.userId}"
                .isRootAdmin="${ctx.isRootAdmin}"></role-view>
  `)}
  ${ctx.loggedInUserId !== ctx.userId && !ctx.isRootAdmin ? html`
    <button type="button" class="btn btn-link" ?disabled="${!ctx.userId}" @click="${ctx.addRole}">
      <span class="ai ai-create"></span> ${until(ctx.__('<tt-ciDoWJ4a>', 'Add New Role'))}
    </button>`
    : html``}
`;
